import React from 'react';
import { Card, CardBody, CardHeader } from '../../../../components/Atoms/Card';
import Button from '../../../../components/Atoms/Button';
import Badge from '../../../../components/Atoms/Badge';
import IconButton from '../../../../components/Atoms/IconButton';
import DatasetBuildCard, { isBuildActive } from './DatasetBuildCard';
import { useI18n } from '../../../../i18n';
import { resolveMediaUrl } from '../../../../utils/mediaUrl';

/**
 * The holding area. Two views in one card:
 *
 *  - no dataset open: the list of datasets
 *  - a dataset open:  its images, multi-selectable, with a transfer action
 *
 * Selection is what feeds the transfer, so the toolbar stays visible above the
 * grid and reports the count.
 */
const DatasetPanel = ({
  datasets,
  datasetsError,
  activeDataset,
  builds,
  isDownloading,
  datasetItems,
  datasetTotal,
  isDatasetLoading,
  selectedItemIds,
  handlers,
}) => {
  const { t } = useI18n();
  const selectedCount = selectedItemIds.size;
  const allShownSelected = datasetItems.length > 0 && selectedCount >= datasetItems.length;

  // ---------------------------------------------------------------- list view
  if (!activeDataset) {
    return (
      <Card className="v2i-card">
        <CardHeader
          title={t('videoToImages.datasets.heading')}
          actions={
            <IconButton icon="fa-sync" title={t('common.refresh')} onClick={handlers.handleGetDatasets} />
          }
        />
        <CardBody>
          {datasetsError && (
            <div className="v2i-alert v2i-alert--danger" role="alert">
              <i className="fa fa-exclamation-circle mr-1" aria-hidden="true"></i>{datasetsError}
            </div>
          )}

          {!datasets.length ? (
            <div className="v2i-empty">
              <i className="fa fa-database v2i-empty__icon" aria-hidden="true"></i>
              <p className="v2i-empty__title mb-1">{t('videoToImages.datasets.empty')}</p>
              <p className="v2i-hint mb-0">{t('videoToImages.datasets.emptyHint')}</p>
            </div>
          ) : (
            <>
              <p className="v2i-hint">{t('videoToImages.datasets.subtitle')}</p>
              <div className="v2i-dataset-grid">
                {datasets.map((dataset) => (
                  <button
                    key={dataset._id}
                    type="button"
                    className="v2i-dataset-card"
                    onClick={() => handlers.handleOpenDataset(dataset._id)}
                  >
                    <span className="v2i-dataset-card__thumb">
                      {dataset.previewUrl ? (
                        <img src={resolveMediaUrl(dataset.previewUrl)} alt="" loading="lazy" />
                      ) : (
                        <i className="fa fa-database" aria-hidden="true"></i>
                      )}
                    </span>
                    <span className="v2i-dataset-card__body">
                      <span className="v2i-dataset-card__name" title={dataset.name}>{dataset.name}</span>
                      <span className="v2i-dataset-card__count">
                        {t('videoToImages.datasets.images', { count: dataset.itemCount || 0 })}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </CardBody>
      </Card>
    );
  }

  // -------------------------------------------------------------- detail view
  return (
    <Card className="v2i-card">
      <CardHeader>
        <div className="v2i-dataset-head">
          <Button variant="ghost" size="sm" onClick={handlers.handleCloseDataset}>
            <i className="fa fa-arrow-left mr-1" aria-hidden="true"></i>
            {t('videoToImages.datasets.backToList')}
          </Button>
          <h5 className="mb-0 v2i-dataset-head__name" title={activeDataset.name}>
            {activeDataset.name}
          </h5>
          <Badge variant="neutral" icon="fa-image">
            {t('videoToImages.datasets.images', { count: datasetTotal })}
          </Badge>
        </div>
        <div className="v2i-dataset-head__actions">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={isDownloading || !datasetTotal}
            tooltipText={t('videoToImages.datasets.downloadAllTooltip')}
            onClick={() => handlers.handleDownloadDataset('all')}
          >
            {isDownloading
              ? <><i className="fa fa-spinner fa-spin mr-1" aria-hidden="true"></i>{t('videoToImages.datasets.downloading')}</>
              : <><i className="fa fa-download mr-1" aria-hidden="true"></i>{t('videoToImages.datasets.downloadAll')}</>}
          </Button>
          <IconButton
            icon="fa-trash"
            className="v2i-icon-btn--danger"
            title={t('videoToImages.datasets.deleteDataset')}
            onClick={() => handlers.handleDeleteDataset(activeDataset._id)}
          />
        </div>
      </CardHeader>
      <CardBody>
        <div className="v2i-select-bar">
          <div className="v2i-select-bar__left">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={allShownSelected ? handlers.clearItemSelection : handlers.handleSelectAllItems}
            >
              <i className={`fa ${allShownSelected ? 'fa-square' : 'fa-check-square'} mr-1`} aria-hidden="true"></i>
              {allShownSelected
                ? t('videoToImages.datasets.clearSelection')
                : t('videoToImages.datasets.selectAll')}
            </Button>
            <span className="v2i-select-bar__count">
              {selectedCount
                ? t('videoToImages.datasets.selected', { count: selectedCount })
                : t('videoToImages.datasets.selectHint')}
            </span>
          </div>
          <div className="v2i-select-bar__right">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!selectedCount || isDownloading}
              tooltipText={t('videoToImages.datasets.downloadTooltip')}
              onClick={() => handlers.handleDownloadDataset('selected')}
            >
              <i className="fa fa-download mr-1" aria-hidden="true"></i>
              {t('videoToImages.datasets.download')}
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              disabled={!selectedCount}
              onClick={handlers.handleRemoveSelected}
            >
              <i className="fa fa-trash-alt mr-1" aria-hidden="true"></i>
              {t('videoToImages.datasets.removeSelected')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!selectedCount}
              onClick={handlers.handleOpenBuild}
            >
              <i className="fa fa-magic mr-1" aria-hidden="true"></i>
              {t('videoToImages.datasets.transfer')}
            </Button>
          </div>
        </div>

        {(builds || []).filter((build) => isBuildActive(build) || build.result || build.error)
          .slice(0, 3)
          .map((build) => (
            <DatasetBuildCard key={build._id} build={build} handlers={handlers} />
          ))}

        <div className="v2i-image-grid">
          {datasetItems.map((item) => {
            const isSelected = selectedItemIds.has(item._id);
            const sentTo = (item.transfers || []).length;
            return (
              <div
                key={item._id}
                className={`v2i-image-tile ${isSelected ? 'v2i-image-tile--selected' : ''}`}
                role="checkbox"
                aria-checked={isSelected}
                tabIndex={0}
                title={item.name}
                onClick={() => handlers.toggleItemSelection(item._id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handlers.toggleItemSelection(item._id);
                  }
                }}
              >
                <img
                  className="v2i-image-tile__img"
                  src={resolveMediaUrl(item.thumbnailUrlDisplay || item.urlDisplay)}
                  alt={item.name}
                  loading="lazy"
                />
                <span className="v2i-image-tile__check" aria-hidden="true">
                  <i className={isSelected ? 'fa fa-check-circle' : 'far fa-circle'}></i>
                </span>
                {!!sentTo && (
                  <span
                    className="v2i-image-tile__sent"
                    title={t('videoToImages.datasets.alreadySent', { count: sentTo })}
                  >
                    <i className="fa fa-share-square" aria-hidden="true"></i>
                  </span>
                )}
                <span className="v2i-image-tile__name">{item.name}</span>
              </div>
            );
          })}
        </div>

        <div className="v2i-dataset-foot">
          <span className="v2i-hint">
            {t('videoToImages.datasets.showing', { shown: datasetItems.length, total: datasetTotal })}
          </span>
          {datasetItems.length < datasetTotal && (
            <Button variant="outline-secondary" size="sm"
              disabled={isDatasetLoading} onClick={handlers.handleLoadMoreItems}>
              {isDatasetLoading ? t('common.loading') : t('videoToImages.datasets.loadMore')}
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default DatasetPanel;
